import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useSEO, buildFaqSchema, buildBreadcrumbSchema } from '../utils/seo';
import { ArrowRight, Sparkles } from 'lucide-react';

/**
 * Lightweight, SEO-friendly landing page used for tool pages, persona pages,
 * and legal/template pages. Each consuming page passes its own props.
 */
const SeoLandingPage = ({
  pretitle,
  title,
  titleAccent,
  description,
  bullets = [],
  ctaLabel,
  ctaTo,
  secondaryCtaLabel,
  secondaryCtaTo,
  testIdPrefix,
  faq = [],
  seoTitle,
  seoDescription,
  breadcrumbs,
  softwareApplication,
}) => {
  const canonical = typeof window !== 'undefined' ? window.location.href : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const crumbs = breadcrumbs || [
    { name: 'Home', url: '/' },
    { name: title, url: path },
  ];

  const swApp = softwareApplication ? {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: softwareApplication.name,
    applicationCategory: softwareApplication.category || 'BusinessApplication',
    applicationSubCategory: softwareApplication.subCategory || 'RealEstateApplication',
    operatingSystem: 'Any (web-based)',
    description: softwareApplication.description || description,
    url: canonical,
    publisher: { '@type': 'Organization', name: 'Shopperden Ltd', url: 'https://shopperden.com' },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP', availability: 'https://schema.org/InStock' },
    aggregateRating: softwareApplication.rating ? {
      '@type': 'AggregateRating',
      ratingValue: softwareApplication.rating.value || '4.8',
      reviewCount: softwareApplication.rating.count || '124',
    } : undefined,
    featureList: softwareApplication.features || undefined,
  } : null;

  const schemas = [
    buildBreadcrumbSchema(crumbs),
    buildFaqSchema(faq),
    swApp,
  ].filter(Boolean);

  useSEO({
    title: seoTitle || `${title} ${titleAccent || ''} — Shopperden`,
    description: seoDescription || description,
    canonical,
    schema: schemas.length ? schemas : undefined,
  });

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid={`${testIdPrefix}-page`}>
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F766E] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FED7AA]" />
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#FED7AA] font-bold">{pretitle}</p>
            </div>
            <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-[4rem]">
              <span className="block">{title}</span>
              {titleAccent && <span className="block text-[#FED7AA] italic">{titleAccent}</span>}
            </h1>
            <p className="mt-6 text-white/75 text-[15px] sm:text-lg leading-relaxed max-w-2xl">{description}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {ctaTo && (
                <Link
                  to={ctaTo}
                  className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white px-7 py-3.5 tracking-widest uppercase font-bold text-sm transition-all hover:scale-105"
                  data-testid={`${testIdPrefix}-primary-cta`}
                >
                  {ctaLabel} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
              {secondaryCtaTo && (
                <Link
                  to={secondaryCtaTo}
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/20 hover:bg-white/15 text-white px-7 py-3.5 tracking-widest uppercase font-bold text-sm transition-all"
                  data-testid={`${testIdPrefix}-secondary-cta`}
                >
                  {secondaryCtaLabel}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bullets */}
      {bullets.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {bullets.map((b, i) => (
              <div key={b.title || `bullet-${i}`} className="bg-white border border-[#E2E8F0] p-7 hover:border-[#E07A3A] transition-colors" data-testid={`${testIdPrefix}-bullet-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#10B981] to-[#0F766E] flex items-center justify-center mb-4">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] mb-2">{b.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ — boosts long-tail SEO */}
      {faq.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pb-20">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] mb-8">Frequently asked</h2>
          <div className="space-y-4">
            {faq.map((q, i) => (
              <details key={q.q || `faq-${i}`} className="group bg-white border border-[#E2E8F0] p-5" data-testid={`${testIdPrefix}-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-[#0F172A] flex justify-between items-center">
                  {q.q}
                  <span className="text-[#E07A3A] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{q.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SeoLandingPage;
